import { useQuery } from '@apollo/client';
import { EFilterType, TFilter, TFilterEvent } from 'components/Filter/filter.types';
import { DUE_DILIGENCE_PROCESSES_PER_PAGE } from 'constants/pagination';
import { GET_GEO_DATA_PROCESSES } from 'graphql/secure/queries/dueDiligence.queries';
import { IGeoDataProcessConnection } from 'graphql/secure/queries/types/geoDataProcess-query.types';
import { useMemo, useState } from 'react';
import { IGeoDataProcess } from 'types/geodata.types';
import { ArrayParam, StringParam, useQueryParams, withDefault } from 'use-query-params';

interface IUseDueDiligenceProcessesProps {
  // Can be used to skip the useQueryParam search and use the internal search term instead
  skipUseQueryParams?: boolean;
  defaultParnterIds?: string[];
  defaultRawMaterialIds?: string[];
  processesPerPage?: number;
}

interface IQueryState {
  partnerIds?: string[] | null;
  rawMaterialIds?: string[] | null;
  order: 'ASC' | 'DESC' | undefined;
}

const useDueDiligenceProcesses = ({
  skipUseQueryParams,
  defaultParnterIds,
  defaultRawMaterialIds,
  processesPerPage = DUE_DILIGENCE_PROCESSES_PER_PAGE,
}: IUseDueDiligenceProcessesProps = {}) => {
  const [query, setQuery] = useQueryParams(
    {
      partnerIds: withDefault(ArrayParam, defaultParnterIds || null),
      rawMaterialIds: withDefault(ArrayParam, defaultRawMaterialIds || null),
      order: withDefault(StringParam, 'DESC'),
    },
    { removeDefaultsFromUrl: true }
  );

  const [internalQueryState, setInternalQueryState] = useState<IQueryState>({
    partnerIds: defaultParnterIds || null,
    rawMaterialIds: defaultRawMaterialIds || null,
    order: 'DESC',
  });

  const { partnerIds, rawMaterialIds, order } = skipUseQueryParams ? internalQueryState : query;

  const orderByMethod = order as 'ASC' | 'DESC' | undefined;

  const { data, loading, error, refetch, fetchMore } = useQuery<{
    geoDataProcesses: IGeoDataProcessConnection;
  }>(GET_GEO_DATA_PROCESSES, {
    variables: {
      filters: {
        ...(partnerIds && { partnerIds }),
        ...(rawMaterialIds && { rawMaterialIds }),
      },
      orderBy: {
        orderByMethod,
      },
      first: processesPerPage,
    },
    fetchPolicy: 'network-only',
  });

  const hasNextPage: boolean = !!data?.geoDataProcesses?.pageInfo?.hasNextPage;
  const endCursor = data?.geoDataProcesses?.pageInfo?.endCursor;
  const totalCount = data?.geoDataProcesses?.count;
  const geoDataProcessesSummary = data?.geoDataProcesses?.summary;

  const dueDiligenceProcesses: IGeoDataProcess[] = useMemo(() => {
    if (!data) return [];
    return data?.geoDataProcesses.edges.map(edge => edge.node);
  }, [data]);

  const filters: TFilter<EFilterType.PARTNERS | EFilterType.RAW_MATERIALS>[] = useMemo(
    () => [
      {
        type: EFilterType.PARTNERS,
        value: partnerIds
          ? (partnerIds.filter((id): id is string => id !== null) as string[])
          : undefined,
      },
      {
        type: EFilterType.RAW_MATERIALS,
        value: rawMaterialIds
          ? (rawMaterialIds.filter((id): id is string => id !== null) as string[])
          : undefined,
      },
    ],
    [partnerIds, rawMaterialIds]
  );

  const handlePageEndReached = () => {
    if (endCursor) {
      fetchMore({
        variables: {
          after: endCursor,
        },
      });
    }
  };

  const handleFilterReset = () => {
    skipUseQueryParams
      ? setInternalQueryState(prev => ({
          ...prev,
          partnerIds: undefined,
          rawMaterialIds: undefined,
        }))
      : setQuery(prev => ({
          ...prev,
          partnerIds: undefined,
          rawMaterialIds: undefined,
        }));
  };

  const handleFilterChange = (event: TFilterEvent) => {
    if (event.type === EFilterType.PARTNERS) {
      skipUseQueryParams
        ? setInternalQueryState(prev => ({
            ...prev,
            partnerIds: event.value || undefined,
          }))
        : setQuery(prev => ({
            ...prev,
            partnerIds: event.value || undefined,
          }));
    } else if (event.type === EFilterType.RAW_MATERIALS) {
      skipUseQueryParams
        ? setInternalQueryState(prev => ({
            ...prev,
            rawMaterialIds: event.value || undefined,
          }))
        : setQuery(prev => ({
            ...prev,
            rawMaterialIds: event.value || undefined,
          }));
    }
  };

  return {
    dueDiligenceProcesses,
    geoDataProcessesSummary,
    hasNextPage,
    loading,
    error,
    filters,
    totalCount,
    handlePageEndReached,
    refetch,
    handleFilterChange,
    handleFilterReset,
  };
};

export default useDueDiligenceProcesses;
